import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Star, MapPin, Clock, Phone, Building, Calendar,
  Stethoscope, Award, FileText, User, Video, Home, Loader2, Heart, BadgeCheck,
} from 'lucide-react';
import { apiRequest } from '../lib/utils';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { ShareProfileDialog } from '../components/ShareProfileDialog';

const PHYSIGN_LOGO = "https://customer-assets.emergentagent.com/job_4bf5f59f-ff2a-4b89-ab5f-09ffb3b85c36/artifacts/91dqnx5f_high-resolution-color-logo-3.png";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  const filename = avatar.replace(/^\/?api\/uploads\//, '').replace(/^\/?uploads\//, '');
  return `${API_BASE}/api/uploads/${filename}`;
};

const serviceIcons = {
  clinic: { icon: Building, label: 'Clinic Visit' },
  clinic_visit: { icon: Building, label: 'Clinic Visit' },
  video_consultation: { icon: Video, label: 'Video Consult' },
  home_visit: { icon: Home, label: 'Home Visit' },
};

export default function ProviderProfile() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileData, reviewsData] = await Promise.all([
          apiRequest(`/providers/${providerId}`),
          apiRequest(`/reviews/provider/${providerId}`).catch(() => []),
        ]);
        setProvider(profileData);
        setReviews(Array.isArray(reviewsData) ? reviewsData : []);
      } catch (err) {
        toast.error('Provider profile not found');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [providerId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F62FE]" />
      </div>
    );
  }

  if (!provider) return null;

  const { profile, user: providerUser } = provider;
  const avatarUrl = getAvatarUrl(providerUser?.avatar);
  const services = profile?.services || [];
  const availability = profile?.availability || {};
  const dayNames = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const isNurse = providerUser?.role === 'nurse';

  // Parse nurse services from bio JSON
  let nurseServices = [];
  if (isNurse && profile?.bio) {
    try {
      const parsed = JSON.parse(profile.bio);
      if (parsed.nurse_data?.nurse_services) {
        nurseServices = parsed.nurse_data.nurse_services.filter(s => s.name && s.price && s.pricing_type);
      }
    } catch { /* not JSON, ignore */ }
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="provider-profile-page">
      {(() => {
        const role = provider?.user?.role || 'doctor';
        const name = provider?.user?.name || 'Healthcare Provider';
        const spec = provider?.profile?.specialization;
        const city = provider?.profile?.city || provider?.profile?.location;
        const rating = provider?.profile?.rating;
        const fee = provider?.profile?.consultation_fee || provider?.profile?.home_visit_fee;
        const seoTitle = `${role === 'doctor' ? 'Dr. ' : ''}${name}${spec ? ` - ${spec}` : ''}${city ? ` in ${city}` : ''}`;
        const seoDesc = `Book ${spec ? spec.toLowerCase() + ' ' : ''}${role === 'doctor' ? 'Dr. ' : ''}${name}${city ? ` in ${city}` : ''} on Physign. ${fee ? `Consultation fee ₹${fee}. ` : ''}Verified provider with same-day availability. Book online now.`;
        const schema = {
          "@context": "https://schema.org",
          "@type": role === 'doctor' ? "Physician" : "MedicalBusiness",
          "name": (role === 'doctor' ? 'Dr. ' : '') + name,
          "url": `https://physign.com/provider/${providerId}`,
          "image": provider?.user?.avatar || undefined,
          ...(spec && { "medicalSpecialty": spec }),
          ...(city && { "areaServed": city }),
          ...(rating && {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": rating,
              "bestRating": 5,
              "reviewCount": provider?.profile?.review_count || 1
            }
          }),
          ...(fee && {
            "priceRange": `₹${fee}`,
            "offers": {
              "@type": "Offer",
              "price": fee,
              "priceCurrency": "INR"
            }
          }),
        };
        return (
          <SEO
            path={`/provider/${providerId}`}
            title={seoTitle}
            description={seoDesc}
            image={provider?.user?.avatar || undefined}
            schema={schema}
          />
        );
      })()}
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>Physign</span>
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Card */}
        <Card className="overflow-hidden" data-testid="profile-hero">
          <div className="h-32 sm:h-40 relative bg-gradient-to-r from-[#3B1C73] to-[#5B2FAA]" data-testid="profile-cover">
            {profile?.cover_image && (
              <img
                src={profile.cover_image}
                alt=""
                className="w-full h-full object-cover"
                data-testid="profile-cover-image"
              />
            )}
          </div>
          <CardContent className="relative px-6 pb-6">
            <div className="flex flex-col sm:flex-row gap-4 -mt-16">
              <div className="w-28 h-28 rounded-full border-4 border-white bg-slate-100 flex items-center justify-center overflow-hidden shadow-lg flex-shrink-0" data-testid="profile-avatar-large">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={providerUser?.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-12 h-12 text-slate-300" />
                )}
              </div>
              <div className="pt-2 sm:pt-14 flex-1">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="profile-name">
                      {providerUser?.name || 'Healthcare Provider'}
                      {profile?.approval_status === 'approved' && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold" data-testid="profile-verified-badge">
                          <BadgeCheck className="w-4 h-4" />Verified
                        </span>
                      )}
                    </h1>
                    <p className="text-[#0F62FE] font-medium capitalize text-base">
                      {profile?.specialization || providerUser?.role}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">
                      {profile?.qualification} {profile?.experience_years ? `• ${profile.experience_years}+ years experience` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <ShareProfileDialog
                      providerId={providerId}
                      name={providerUser?.name}
                      specialization={profile?.specialization}
                      role={providerUser?.role}
                      testId="profile-share-btn"
                    />
                    <Badge 
                      className={`px-3 py-1.5 text-sm font-semibold ${providerUser?.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}
                      data-testid="profile-availability-badge"
                    >
                      {providerUser?.is_active !== false ? 'Available' : 'Not Available'}
                    </Badge>
                    <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1.5 rounded-lg">
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <span className="font-bold text-lg">{profile?.rating || '0'}</span>
                      <span className="text-slate-500 text-sm">({profile?.total_reviews || 0} reviews)</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="md:col-span-2 space-y-6">
            {/* About */}
            {profile?.bio && (
              <Card data-testid="profile-about">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">About</h2>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {(() => {
                      try {
                        const parsed = JSON.parse(profile.bio);
                        return parsed.bio_text || '';
                      } catch {
                        return profile.bio;
                      }
                    })()}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Professional Details */}
            <Card data-testid="profile-details">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Professional Details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {profile?.qualification && (
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Award className="w-5 h-5 text-[#0F62FE] mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Qualification</p>
                        <p className="font-medium text-slate-900">{profile.qualification}</p>
                      </div>
                    </div>
                  )}
                  {profile?.specialization && (
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Stethoscope className="w-5 h-5 text-[#0F62FE] mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Specialization</p>
                        <p className="font-medium text-slate-900 capitalize">{profile.specialization}</p>
                      </div>
                    </div>
                  )}
                  {profile?.experience_years && (
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Clock className="w-5 h-5 text-[#0F62FE] mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Experience</p>
                        <p className="font-medium text-slate-900">{profile.experience_years}+ years</p>
                      </div>
                    </div>
                  )}
                  {profile?.clinic_name && (
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Building className="w-5 h-5 text-[#0F62FE] mt-0.5" />
                      <div>
                        <p className="text-xs text-slate-500">Clinic</p>
                        <p className="font-medium text-slate-900">{profile.clinic_name}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Services */}
            {services.filter(s => serviceIcons[s]).length > 0 && (
              <Card data-testid="profile-services">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">Services Offered</h2>
                  <div className="flex flex-wrap gap-3">
                    {services.filter(s => serviceIcons[s]).map((s) => {
                      const svc = serviceIcons[s];
                      return (
                        <div key={s} className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#0F62FE] rounded-lg text-sm font-medium">
                          <svc.icon className="w-4 h-4" />
                          {svc.label}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Nurse Services & Pricing */}
            {isNurse && nurseServices.length > 0 && (
              <Card data-testid="nurse-services-pricing">
                <CardContent className="p-6">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5 text-[#0F62FE]" />
                    Services & Pricing
                  </h2>
                  <div className="space-y-2">
                    {nurseServices.map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-3 px-4 bg-slate-50 rounded-lg border border-slate-100" data-testid={`nurse-service-item-${i}`}>
                        <span className="text-sm font-medium text-slate-800">{s.name}</span>
                        <span className="text-sm font-semibold text-[#0F62FE]">₹{s.price} / {s.pricing_type}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Availability — reads new wizard structure availability.weekly.days[day].enabled */}
            {(availability?.weekly?.days || Object.keys(availability).length > 0) && (
              <Card data-testid="profile-availability">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-slate-900">Availability</h2>
                    <span className="text-xs text-slate-400">Tap an open day to book</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {dayNames.map((day) => {
                      // New wizard shape: availability.weekly.days[day].enabled + slots
                      // Legacy shape: availability[day] === true/false
                      const wizardDay = availability?.weekly?.days?.[day] || availability?.weekly?.[day];
                      const isAvailable = wizardDay ? Boolean(wizardDay.enabled) : Boolean(availability?.[day]);
                      const slotCount = wizardDay?.slots?.length || 0;
                      return (
                        <button
                          type="button"
                          key={day}
                          onClick={() => {
                            if (!isAvailable || providerUser?.is_active === false) return;
                            navigate(`/book/${providerId}?day=${day}`);
                          }}
                          disabled={!isAvailable}
                          data-testid={`avail-day-${day}`}
                          className={`px-3 py-2 rounded-lg text-center text-sm font-medium capitalize transition-all ${
                            isAvailable
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:shadow-sm cursor-pointer'
                              : 'bg-slate-50 text-slate-400 border border-transparent cursor-not-allowed'
                          }`}
                        >
                          {day.slice(0, 3)}
                          <span className="block text-xs mt-0.5">
                            {isAvailable ? (slotCount > 0 ? `${slotCount} slot${slotCount > 1 ? 's' : ''}` : 'Available') : 'Off'}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            <Card data-testid="profile-reviews">
              <CardContent className="p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">
                  Patient Reviews ({reviews.length})
                </h2>
                {reviews.length === 0 ? (
                  <p className="text-slate-500 text-sm">No reviews yet</p>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((review, i) => (
                      <div key={i} className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center text-xs font-bold text-[#0F62FE]">
                              {review.patient_name?.[0]?.toUpperCase() || 'P'}
                            </div>
                            <span className="font-medium text-slate-900 text-sm">{review.patient_name || 'Patient'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, j) => (
                              <Star key={j} className={`w-3.5 h-3.5 ${j < review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                            ))}
                          </div>
                        </div>
                        {review.review_text && <p className="text-sm text-slate-600">{review.review_text}</p>}
                        <p className="text-xs text-slate-400 mt-2">{new Date(review.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Card */}
          <div className="space-y-4">
            <Card className="sticky top-20" data-testid="booking-card">
              <CardContent className="p-6 space-y-4">
                {!isNurse && (services.includes('clinic') || (!services.length)) && (
                  <div>
                    <p className="text-sm text-slate-500">Consultation Fee</p>
                    <p className="text-3xl font-bold text-slate-900">₹{profile?.consultation_fee || 500}</p>
                  </div>
                )}
                {(services.includes('home_visit') || isNurse) && (
                  <div>
                    <p className="text-sm text-slate-500">Home Visit Fee</p>
                    <p className="text-3xl font-bold text-slate-900">₹{profile?.home_visit_fee || 1000}</p>
                  </div>
                )}
                <Button
                  className={`w-full h-12 text-base rounded-lg ${providerUser?.is_active !== false ? 'bg-[#0F62FE] hover:bg-[#0050E6] text-white' : 'bg-slate-200 text-slate-500 cursor-not-allowed'}`}
                  onClick={() => providerUser?.is_active !== false && navigate(`/book/${providerId}`)}
                  disabled={providerUser?.is_active === false}
                  data-testid="book-appointment-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {providerUser?.is_active !== false ? 'Book Appointment' : 'Currently Unavailable'}
                </Button>

                {/* Contact Info — CLINIC only (never expose provider's personal phone/email) */}
                {(() => {
                  // Derive clinic phone from wizard practice_locations (Step 4); fallback to no phone.
                  const primaryLocation = (profile?.practice_locations || []).find(l => l?.is_current) || (profile?.practice_locations || [])[0] || null;
                  const clinicPhone = primaryLocation?.phone || profile?.clinic_phone || null;
                  const clinicName = primaryLocation?.clinic_name || primaryLocation?.name || profile?.clinic_name || null;
                  const clinicAddress = primaryLocation?.address || profile?.clinic_address || profile?.location || null;
                  return (
                    <div className="pt-4 border-t border-slate-100 space-y-3" data-testid="clinic-contact">
                      {clinicName && (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Building className="w-4 h-4 text-slate-400" />
                          <span data-testid="clinic-name">{clinicName}</span>
                        </div>
                      )}
                      {clinicAddress && (
                        <div className="flex items-start gap-2 text-sm text-slate-600">
                          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                          <span data-testid="clinic-address">{clinicAddress}</span>
                        </div>
                      )}
                      {clinicPhone && (
                        <a
                          href={`tel:${clinicPhone}`}
                          className="flex items-center gap-2 text-sm text-[#3B1C73] hover:underline font-medium"
                          data-testid="clinic-phone"
                        >
                          <Phone className="w-4 h-4" />
                          <span>{clinicPhone}</span>
                          <span className="text-xs text-slate-400 ml-auto">Clinic</span>
                        </a>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
